import { Col, Input, Pagination, Row, Table, TableColumnsType } from "antd";
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";

import { toArray, workOrderApi } from "../../../../Services";
import { getFormattedDate } from "../../../../Utils/CommonUtils";
import styles from "../../WorkOrder.module.scss";

interface Props {
  envelopeId?: string;
  /** Bumped by the parent after a workflow action, which writes a log entry. */
  refreshKey?: number;
}

const PAGE_SIZE = 10;

/* Details can arrive URL-encoded — the SPA's Logs tab decodes the same field.
   Text that merely contains a "%" is shown as it is. */
const readable = (value?: string) => {
  if (!value) return "-";
  try {
    const decoded = decodeURIComponent(value);
    return decoded.replace(/\b(\d+)\.(\d+)\b/g, (_: string, major: string, minor: string) => {
      const version = (Number(major) - 1) * 10 + Number(minor);
      return `V${version}`;
    });
  } catch {
    return value;
  }
};

/**
 * Detail — Logs tab.
 *
 * The work order's audit trail from `GET /auditlog/envelope/get-all-logs`,
 * newest first. Like Comments, it is paged, so page, sort and search go to the
 * server rather than being applied to the rows already fetched.
 */
export const Logs = ({ envelopeId, refreshKey = 0 }: Props) => {
  const [rows, setRows] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("actionPerformedOn");
  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState("");

  /* Search once typing pauses, from the first page. */
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(search.trim());
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!envelopeId) return;
    /* A slower, older request must not overwrite the rows of a newer one. */
    let current = true;
    setIsLoading(true);
    workOrderApi
      .logs({ envelopeId, page, size: PAGE_SIZE, searchText, order, orderBy })
      .then((result: any) => {
        if (!current) return;
        /* Two entries can share an action and a timestamp, so the key is the
           row's place in the trail unless the API gives it an id. */
        setRows(toArray(result).map((row: any, index) => ({ ...row, key: row.id ?? `${page}-${index}` })));
        setTotalRecords(result?.totalCount ?? 0);
      })
      .catch(() => current && setRows([]))
      .finally(() => current && setIsLoading(false));
    return () => {
      current = false;
    };
  }, [envelopeId, page, searchText, order, orderBy, refreshKey]);

  const columns: TableColumnsType<any> = [
    { title: "Action", dataIndex: "action", sorter: true, width: 180, render: (value) => value || "-" },
    { title: "Details", dataIndex: "detail", sorter: true, render: readable },
    {
      title: "Performed By",
      dataIndex: "actionPerformedBy",
      sorter: true,
      width: 220,
      render: (value) => value || "-",
    },
    {
      title: "Performed At",
      dataIndex: "actionPerformedOn",
      sorter: true,
      width: 200,
      render: (value) => getFormattedDate(value) || "-",
    },
  ];

  return (
    <>
      <Row style={{ marginBottom: 16 }}>
        <Col
          xs={24}
          sm={10}
        >
          <Input
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            prefix={
              <Icon
                icon="ic:outline-search"
                className={styles.viewIcon}
              />
            }
          />
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={rows}
        loading={isLoading}
        pagination={false}
        rowKey="key"
        scroll={{ x: "max-content" }}
        onChange={(_pagination, _filters, sorter: any) => {
          if (!sorter?.field) return;
          setOrderBy(String(sorter.field));
          setOrder(sorter.order === "ascend" ? "asc" : "desc");
          setPage(0);
        }}
      />
      <Pagination
        style={{ marginTop: 16, textAlign: "right" }}
        current={page + 1}
        pageSize={PAGE_SIZE}
        total={totalRecords}
        showSizeChanger={false}
        onChange={(next) => setPage(next - 1)}
      />
    </>
  );
};
